/**
 * Paint colour algebra. Implements docs/mixing-table.yaml `rules.order`
 * literally, in the same order, so the prose and the code can be diffed by eye.
 */
import { MIX_TABLE, PALETTE, SHADE_HEX, type Mode } from './table.generated.ts';

export type ColorId = string;

export const MUD = 'mud';
export const BLACK = 'black';
const SHADE_SUFFIX = '_shade';

export function isShaded(c: ColorId): boolean {
  return c.endsWith(SHADE_SUFFIX);
}

/** Strip the shade suffix: `red_shade` -> `red`. */
export function hue(c: ColorId): ColorId {
  return isShaded(c) ? c.slice(0, -SHADE_SUFFIX.length) : c;
}

export function shaded(h: ColorId): ColorId {
  return isShaded(h) ? h : `${h}${SHADE_SUFFIX}`;
}

/**
 * mix(mode, a, b) -- symmetric and total over all 26 colour ids.
 * Rule order is exactly `rules.order` in docs/mixing-table.yaml:
 *   same -> mud -> darken -> same_hue -> table -> default(mud)
 */
export function mix(mode: Mode, a: ColorId, b: ColorId): ColorId {
  // 1. same + same = same (units add; that is the caller's job)
  if (a === b) return a;

  // 2. mud is absorbing
  if (a === MUD || b === MUD) return MUD;

  // 3. black darkens once. black + black was caught by rule 1.
  //    black + X_shade = X_shade (shade does not stack).
  if (a === BLACK || b === BLACK) {
    const other = a === BLACK ? b : a;
    return shaded(hue(other));
  }

  // 4. same hue, different shade state: red + red_shade = red_shade. Sticky.
  if (hue(a) === hue(b)) return shaded(hue(a));

  // 5. the mode's table, on hues; the result inherits shade if either input
  //    was shaded.
  const result = MIX_TABLE[mode]?.[`${hue(a)}+${hue(b)}`];
  if (result === undefined) return MUD;
  return isShaded(a) || isShaded(b) ? shaded(result) : result;
}

/** Every colour id in the game: 12 hues + 12 shades + black + mud. */
export function allColorIds(): ColorId[] {
  const ids: ColorId[] = [];
  for (const [id, def] of Object.entries(PALETTE)) {
    ids.push(id);
    if (def.family === 'chroma') ids.push(shaded(id));
  }
  return ids;
}

/** Display name for the HUD, e.g. `RED DK`. */
export function colorName(c: ColorId): string {
  const base = PALETTE[hue(c)]?.name ?? c.toUpperCase();
  return isShaded(c) ? `${base} DK` : base;
}

/** Hex for rendering. Shades have a reference hex for non-pixel contexts. */
export function colorHex(c: ColorId): string {
  if (isShaded(c)) return SHADE_HEX[c] ?? PALETTE[hue(c)]?.hex ?? '#ff00ff';
  return PALETTE[c]?.hex ?? '#ff00ff';
}

export function colorGlyph(c: ColorId): string {
  return PALETTE[hue(c)]?.unicode ?? '?';
}

export { PALETTE, type Mode };
